import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { navigateTo } from '../services/router.js';
import * as api from '../services/signal-sources-api.js';
import type { SignalSource, SignalSourceType } from '../services/signal-sources-api.js';
import * as companyApi from '../services/company-api.js';
import { listGoogleAccounts, type GoogleAccount } from '../services/google-account-api.js';

interface CompanyLite { id: number; name: string }

/**
 * Signal-Quellen-Verwaltung (compass-new, Migration 116). Adapter (applai/email) per UI
 * konfigurieren statt per Env-Flag: an/aus, Intervall, Params, „Jetzt ausführen".
 * Der Scheduler im Backend liest diese Config und fährt die aktiven Quellen.
 */
@customElement('cp-page-signal-sources')
export class CpPageSignalSources extends LitElement {
  createRenderRoot() { return this; }

  @state() private _sources: SignalSource[] = [];
  @state() private _companies: CompanyLite[] = [];
  @state() private _accounts: GoogleAccount[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _busy = 0;
  @state() private _newType: SignalSourceType = 'applai';
  @state() private _newName = '';

  override connectedCallback(): void {
    super.connectedCallback();
    void this._load();
    void this._loadMeta();
  }

  private async _loadMeta(): Promise<void> {
    try { this._companies = (await companyApi.listCompanies()) as unknown as CompanyLite[]; } catch { /* ignore */ }
    try { this._accounts = await listGoogleAccounts(); } catch { /* ignore */ }
  }

  private async _load(): Promise<void> {
    this._loading = true; this._error = '';
    try { this._sources = await api.listSources(); }
    catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._loading = false; }
  }

  private _companyName(id: unknown): string {
    const c = this._companies.find((x) => x.id === Number(id));
    return c ? c.name : (id != null ? `Firma #${id}` : '—');
  }

  private _defaultConfig(type: SignalSourceType): Record<string, unknown> {
    switch (type) {
      case 'applai': return { status: 'bewerber_aktiv', companyId: 2 };
      case 'email': return { alias: 'primary', userId: 1, companyId: 3 };
      case 'http': return { url: 'https://api.example.com/items', headers: {}, itemsPath: 'data', idField: 'id', titleField: 'title', bodyField: '', companyId: 2 };
      case 'rss': return { url: 'https://example.com/feed.xml', companyId: 2 };
      case 'webhook': return { token: (crypto.randomUUID?.() ?? String(Date.now())).replace(/-/g, ''), companyId: 2 };
      case 'join': return { apiToken: '', companyId: 3, questionSetId: '', jobShortMapping: { '16247704': 'Fullstack', '16247964': 'Fullstack', '16248486': 'Fullstack', '16225012': 'FDAE' } };
      default: return {};
    }
  }

  private _webhookUrl(s: SignalSource): string {
    const token = String((s.config as { token?: string }).token ?? '');
    return `${location.origin}/api/signal-webhook/${token}`;
  }

  private async _add(): Promise<void> {
    if (!this._newName.trim()) { this._error = 'Name fehlt'; return; }
    try {
      await api.createSource({ type: this._newType, name: this._newName.trim(), enabled: false, intervalMs: 300000, config: this._defaultConfig(this._newType) });
      this._newName = '';
      await this._load();
    } catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
  }

  private _field(id: number, key: string): string {
    const el = this.querySelector(`#s${id}-${key}`) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
    return el ? el.value : '';
  }

  /** Baut das config-Objekt aus den strukturierten Feldern (typ-abhängig). */
  private _buildConfig(s: SignalSource): Record<string, unknown> {
    const cfg: Record<string, unknown> = { ...s.config };
    const cid = Number(this._field(s.id, 'companyId'));
    if (cid) cfg.companyId = cid;
    switch (s.type) {
      case 'applai':
        cfg.status = this._field(s.id, 'status');
        break;
      case 'email':
        cfg.alias = this._field(s.id, 'alias') || 'primary';
        cfg.userId = 1;
        break;
      case 'http':
        cfg.url = this._field(s.id, 'url');
        try { cfg.headers = JSON.parse(this._field(s.id, 'headers') || '{}'); } catch { cfg.headers = {}; }
        cfg.itemsPath = this._field(s.id, 'itemsPath');
        cfg.idField = this._field(s.id, 'idField');
        cfg.titleField = this._field(s.id, 'titleField');
        cfg.bodyField = this._field(s.id, 'bodyField');
        break;
      case 'rss':
        cfg.url = this._field(s.id, 'url');
        break;
      case 'webhook':
        cfg.idField = this._field(s.id, 'idField');
        cfg.titleField = this._field(s.id, 'titleField');
        break;
      case 'join':
        cfg.apiToken = this._field(s.id, 'apiToken');
        cfg.questionSetId = this._field(s.id, 'questionSetId');
        try { cfg.jobShortMapping = JSON.parse(this._field(s.id, 'jobShortMapping') || '{}'); } catch { cfg.jobShortMapping = {}; }
        break;
    }
    return cfg;
  }

  private async _save(s: SignalSource): Promise<void> {
    this._busy = s.id; this._error = '';
    try {
      await api.updateSource(s.id, {
        name: this._field(s.id, 'name'),
        intervalMs: Number(this._field(s.id, 'interval')) || 300000,
        config: this._buildConfig(s),
      });
      await this._load();
    } catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._busy = 0; }
  }

  private _companySelect(s: SignalSource) {
    const cur = Number((s.config as { companyId?: number }).companyId);
    if (this._companies.length === 0) {
      return html`<label>Firma-ID (Routing)<input id="s${s.id}-companyId" type="number" .value=${cur ? String(cur) : ''} /></label>`;
    }
    return html`<label>Firma (Routing)
      <select id="s${s.id}-companyId">
        <option value="" ?selected=${!cur}>— keine —</option>
        ${this._companies.map((c) => html`<option value=${c.id} ?selected=${c.id === cur}>${c.name} (#${c.id})</option>`)}
      </select>
    </label>`;
  }

  private _configFields(s: SignalSource) {
    const c = s.config as Record<string, unknown>;
    const txt = (k: string, label: string, ph = '') => html`<label>${label}<input id="s${s.id}-${k}" .value=${String(c[k] ?? '')} placeholder=${ph} /></label>`;
    switch (s.type) {
      case 'applai':
        return html`
          <label>Status
            <select id="s${s.id}-status">
              <option value="bewerber_aktiv" ?selected=${c.status !== 'mitarbeiter_aktiv'}>bewerber_aktiv</option>
              <option value="mitarbeiter_aktiv" ?selected=${c.status === 'mitarbeiter_aktiv'}>mitarbeiter_aktiv</option>
            </select>
          </label>
          ${this._companySelect(s)}`;
      case 'email':
        return html`
          ${this._accounts.length
            ? html`<label>E-Mail-Konto
                <select id="s${s.id}-alias">
                  ${this._accounts.map((a) => html`<option value=${a.account_alias} ?selected=${c.alias === a.account_alias}>${a.google_email} · ${a.account_alias}${a.needs_reauth ? ' ⚠ reauth' : ''}</option>`)}
                </select>
              </label>`
            : html`<label>E-Mail-Konto (Alias) <span class="ss-warn">— kein Google-Konto verbunden (System → Google verbinden)</span>
                <input id="s${s.id}-alias" .value=${String(c.alias ?? 'primary')} /></label>`}
          ${this._companySelect(s)}`;
      case 'http':
        return html`
          ${txt('url', 'URL', 'https://api…')}
          <label>Headers (JSON, z.B. Authorization)<textarea id="s${s.id}-headers" rows="2">${JSON.stringify(c.headers ?? {}, null, 0)}</textarea></label>
          <div class="ss-grid3">
            ${txt('itemsPath', 'itemsPath', 'data')}
            ${txt('idField', 'idField', 'id')}
            ${txt('titleField', 'titleField', 'title')}
          </div>
          ${txt('bodyField', 'bodyField (optional)', '')}
          ${this._companySelect(s)}`;
      case 'rss':
        return html`${txt('url', 'Feed-URL', 'https://…/feed.xml')}${this._companySelect(s)}`;
      case 'webhook':
        return html`
          <div class="ss-grid2">
            ${txt('idField', 'idField', 'id')}
            ${txt('titleField', 'titleField', 'title')}
          </div>
          ${this._companySelect(s)}`;
      case 'join': {
        const mapping = typeof c.jobShortMapping === 'object' && c.jobShortMapping !== null
          ? JSON.stringify(c.jobShortMapping, null, 2)
          : '{}';
        return html`
          <label>API-Token (JOIN_API_TOKEN)<input id="s${s.id}-apiToken" type="password" autocomplete="off" .value=${String(c.apiToken ?? '')} placeholder="eyJ…" /></label>
          ${this._companySelect(s)}
          <label>questionSetId (Applai)<input id="s${s.id}-questionSetId" .value=${String(c.questionSetId ?? '')} placeholder="Applai Question-Set-ID" /></label>
          <label>jobShort-Mapping (Job-ID → Kürzel, JSON)<textarea id="s${s.id}-jobShortMapping" rows="6">${mapping}</textarea></label>`;
      }
      default:
        return nothing;
    }
  }

  private async _toggle(s: SignalSource): Promise<void> {
    this._busy = s.id;
    try { await api.updateSource(s.id, { enabled: !s.enabled }); await this._load(); }
    catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._busy = 0; }
  }

  private async _run(s: SignalSource): Promise<void> {
    this._busy = s.id; this._error = '';
    try { await api.runSource(s.id); await this._load(); }
    catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._busy = 0; }
  }

  private async _del(s: SignalSource): Promise<void> {
    if (!confirm(`Quelle „${s.name}" löschen?`)) return;
    this._busy = s.id;
    try { await api.deleteSource(s.id); await this._load(); }
    catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._busy = 0; }
  }

  private _statusText(s: SignalSource): string {
    if (!s.lastStatus) return 'noch nicht gelaufen';
    if (s.lastStatus.ok) return `OK · ${Number(s.lastStatus.received ?? 0)} neue Signale`;
    return `Fehler: ${String(s.lastStatus.error ?? 'unbekannt')}`;
  }

  override render() {
    return html`
      <style>
        .ss-wrap { padding: 1.5rem; max-width: 920px; margin: 0 auto; font-family: var(--font-family-sans); color: var(--color-text-primary); }
        .ss-head { display: flex; align-items: baseline; gap: 1rem; margin-bottom: .5rem; }
        .ss-head h1 { font-size: 1.4rem; margin: 0; }
        .ss-back { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .3rem .7rem; }
        .ss-refresh { margin-left: auto; cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .3rem .7rem; }
        .ss-intro { color: var(--color-text-secondary); font-size: .88rem; line-height: 1.5; margin-bottom: 1.2rem; }
        .ss-card { border: 1px solid var(--color-border); border-radius: 10px; padding: 1rem 1.1rem; margin: .7rem 0; background: var(--color-bg-secondary); }
        .ss-row { display: flex; align-items: center; gap: .6rem; flex-wrap: wrap; }
        .ss-badge { font-size: .68rem; text-transform: uppercase; letter-spacing: .05em; padding: .15rem .5rem; border-radius: 999px; color: #fff; }
        .ss-badge.applai { background: #1a7fd4; } .ss-badge.email { background: #6a4ea8; }
        .ss-badge.http { background: #2e7d57; } .ss-badge.rss { background: #c8771a; } .ss-badge.webhook { background: #b03a6e; }
        .ss-badge.join { background: #2d6a4f; }
        .ss-hook { margin-top: .5rem; font-size: .76rem; color: var(--color-text-secondary); }
        .ss-hook code { font-family: var(--font-family-mono); background: var(--color-bg-tertiary); border: 1px solid var(--color-border); border-radius: 6px; padding: .2rem .45rem; word-break: break-all; cursor: pointer; }
        .ss-name { flex: 1; min-width: 160px; padding: .35rem .5rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; }
        .ss-int { width: 110px; padding: .35rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; }
        .ss-toggle { cursor: pointer; border: none; border-radius: 999px; padding: .35rem .9rem; font-weight: 600; color: #fff; }
        .ss-toggle.on { background: var(--color-accent-success, #2e7d32); } .ss-toggle.off { background: #888; }
        .ss-form { margin-top: .7rem; display: flex; flex-direction: column; gap: .55rem; }
        .ss-form label { font-size: .74rem; color: var(--color-text-secondary); display: flex; flex-direction: column; gap: .22rem; }
        .ss-form input, .ss-form select, .ss-form textarea { font-size: .86rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; padding: .4rem .5rem; }
        .ss-form textarea { font-family: var(--font-family-mono); font-size: .8rem; }
        .ss-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: .55rem; }
        .ss-grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: .55rem; }
        .ss-warn { color: var(--color-accent-error, #c33); font-weight: 400; }
        .ss-meta { font-size: .78rem; color: var(--color-text-muted); margin-top: .5rem; }
        .ss-actions { display: flex; gap: .4rem; margin-top: .7rem; flex-wrap: wrap; }
        .ss-actions button { cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .35rem .8rem; }
        .ss-run { border-color: #1a7fd4 !important; color: #cfe8ff !important; }
        .ss-del { color: var(--color-accent-error, #c33) !important; border-color: var(--color-accent-error, #c33) !important; margin-left: auto; }
        .ss-add { border: 1px dashed var(--color-border); border-radius: 10px; padding: 1rem; margin-top: 1.2rem; display: flex; gap: .6rem; align-items: center; flex-wrap: wrap; }
        .ss-add select, .ss-add input { padding: .4rem .5rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border: 1px solid var(--color-border); border-radius: 6px; }
        .ss-add button { cursor: pointer; border: none; background: var(--color-accent-primary, #1a7fd4); color: #fff; border-radius: 6px; padding: .45rem 1rem; font-weight: 600; }
      </style>
      <div class="ss-wrap">
        <div class="ss-head">
          <button class="ss-back" @click=${() => navigateTo('/signals')}>← Signale</button>
          <h1>Signal-Quellen</h1>
          <button class="ss-refresh" @click=${() => void this._load()}>↻</button>
        </div>
        <p class="ss-intro">
          Quellen liefern Signale in den Event-Log. Typen:
          <strong>applai</strong> (Matchings, braucht Creds) · <strong>email</strong> (verbundenes Gmail) ·
          <strong>http/json</strong> (jede REST-API, z.B. X/Twitter — <code>config.url</code> + <code>headers</code> + <code>itemsPath</code>/<code>titleField</code>) ·
          <strong>rss/atom</strong> (Feed, nur <code>url</code>) · <strong>webhook</strong> (Push: externes System POSTet an die Ingress-URL) ·
          <strong>join</strong> (JOIN.com Recruiting — Bewerber im SCREENING-Status → Playbook-Run).
          <code>config.companyId</code> bestimmt, gegen welche Firma die Decision routet. Aktive Poll-Quellen laufen im Intervall;
          danach Decision + Projektion automatisch → Signale erscheinen auf der Signale-Seite.
        </p>

        ${this._error ? html`<p style="color:var(--color-accent-error,#c33)">${this._error}</p>` : nothing}
        ${this._loading ? html`<p>Lädt…</p>` : nothing}

        ${this._sources.length === 0 && !this._loading
          ? html`<p style="color:var(--color-text-muted)">Noch keine Quelle. Unten anlegen.</p>` : nothing}

        ${this._sources.map((s) => html`
          <div class="ss-card">
            <div class="ss-row">
              <span class="ss-badge ${s.type}">${s.type}</span>
              <input id="s${s.id}-name" class="ss-name" .value=${s.name} />
              <label style="font-size:.78rem;color:var(--color-text-secondary)">Intervall (ms)
                <input id="s${s.id}-interval" class="ss-int" type="number" .value=${String(s.intervalMs)} />
              </label>
              <button class="ss-toggle ${s.enabled ? 'on' : 'off'}" ?disabled=${this._busy === s.id} @click=${() => void this._toggle(s)}>
                ${s.enabled ? '● Aktiv' : '○ Inaktiv'}
              </button>
            </div>
            <div class="ss-form">
              ${this._configFields(s)}
            </div>
            <div class="ss-meta">Letzter Lauf: ${s.lastRunAt ?? '—'} · ${this._statusText(s)}</div>
            ${s.type === 'webhook' ? html`
              <div class="ss-hook">Ingress-URL (POST dein Item hierher):
                <code title="Klicken zum Kopieren" @click=${() => void navigator.clipboard?.writeText(this._webhookUrl(s))}>${this._webhookUrl(s)}</code>
              </div>
            ` : nothing}
            <div class="ss-actions">
              <button ?disabled=${this._busy === s.id} @click=${() => void this._save(s)}>Speichern</button>
              ${s.type !== 'webhook' ? html`<button class="ss-run" ?disabled=${this._busy === s.id} @click=${() => void this._run(s)}>▶ Jetzt ausführen</button>` : nothing}
              <button class="ss-del" ?disabled=${this._busy === s.id} @click=${() => void this._del(s)}>Löschen</button>
            </div>
          </div>
        `)}

        <div class="ss-add">
          <select @change=${(e: Event) => { this._newType = (e.target as HTMLSelectElement).value as SignalSourceType; }}>
            <option value="applai">applai</option>
            <option value="email">email</option>
            <option value="http">http/json</option>
            <option value="join">join (Recruiting)</option>
            <option value="rss">rss/atom</option>
            <option value="webhook">webhook</option>
          </select>
          <input placeholder="Name der Quelle" .value=${this._newName} @input=${(e: Event) => { this._newName = (e.target as HTMLInputElement).value; }} />
          <button @click=${() => void this._add()}>+ Quelle anlegen</button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-signal-sources': CpPageSignalSources;
  }
}
